import React, { useState, useEffect, useCallback } from "react";
import Select, { Async } from "react-select";
import { debounce } from "lodash";
import { Switch, Text, Loading } from '@nextui-org/react';
import { connect } from "react-redux";

import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";
import { error } from "../../lib/userAlerts";
import { secureFetch } from "../../lib/api";
import { searchOrderList } from "../../lib/utils";
import checkAccess from '../../lib/checkAccess';

import ListItems from "../../components/PageSelectionComponent/ListItems";
import SubPageHeader from "../../components/SubPageHeader";

const height = window.innerHeight;

const ShipmentDocuments = ({ clientId, Access, location }) => {
  const [state, setState] = useState({
    orderNo: "",
    OS: 0,
    stopId: "",
    stopName: "",
    driverId: "",
    bolNumber: "",
    withReview: 0,
    noOfFilter: 0,
    clicked: false,
    orderList: [],
    artifactsList: [],
    activeArtifactsList: [],
    bolList: [],
    orderNoList: [],
    loading: false,
    selectedPage: 1,
    count: 7,
    totalPages: 0,
    trimmedOrderList: [],
    coordinatorId: "",
    openPopover: false,
    openPopoverUpload: false,
    openPopoverDelete: false,
    csvShipmentList: [],
    shipmentFile: null,
    uploadedCount: 0,
    failedCount: 0,
    selectedOrders: {},
    previewDoc: "",
    pdfLoading: false,
    osdDocType: "",
    isBroker: 0,
    partnerDocument: false,
    orderWithDocuments: false
  });

  const getOrderNO = useCallback((input, callback) => {
    let orderNumbers = [];

    if (state.orderNoList?.length > 0) {
      orderNumbers = searchOrderList(state.orderNoList, input);
    }

    if (orderNumbers?.length > 0) {
      callback(null, {
        options: orderNumbers,
        complete: true
      });
    } else {
      secureFetch(endpoint.getOrderNO, {
        filter: input,
        os: state.OS,
        osFilterRequired: false,
      })
        .then(([ok, status, json]) => {
          if (ok) {
            const newOrderNumbers = json.orderNo.map((k) => ({
              label: k.order_id,
              value: k.order_id,
            }));

            setState(prev => ({
              ...prev,
              orderNoList: newOrderNumbers
            }));

            orderNumbers = newOrderNumbers;
          } else {
            error("Oops! Unable to fetch order number list.");
          }
        })
        .finally(() => {
          callback(null, {
            options: orderNumbers,
            complete: true
          });
        });
    }
  }, [state.orderNoList, state.OS]);

  const getBolList = useCallback((input, callback) => {
    let bolNumbers = [];

    if (state.bolList?.length > 0) {
      bolNumbers = searchOrderList(state.bolList, input);
    }

    if (bolNumbers?.length > 0) {
      callback(null, {
        options: bolNumbers,
        complete: true
      });
    } else {
      secureFetch(endpoint.getBolList, {
        filter: input,
        os: state.OS,
        osFilterRequired: false,
      })
        .then(([ok, status, json]) => {
          if (ok) {
            const newBolNumbers = json.bolList.map(k => ({
              label: k.bol_number,
              value: k.bol_number
            }));

            setState(prev => ({
              ...prev,
              bolList: newBolNumbers
            }));

            bolNumbers = newBolNumbers;
          } else {
            error("Oops! Unable to fetch BOL number list.");
          }
        })
        .finally(() => {
          callback(null, {
            options: bolNumbers,
            complete: true
          });
        });
    }
  }, [state.bolList, state.OS]);

  const fetchCompanyDetails = useCallback(async () => {
    try {
      const [ok, status, company] = await secureFetch(endpoint.getCompanyDetails);
      if (ok) {
        setState(prev => ({ ...prev, isBroker: company.is_broker }));
      } else {
        error("Oops! error occurred during fetch company details");
      }
    } catch (err) {
      error("Oops! error occurred during fetch company details");
    }
  }, []);

  const getArtifactsList = useCallback(async () => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.getArtifactsList, {});
      if (ok) {
        const activeArtifactsList = json.artifactsList.filter(item => {
          if (item.status !== 0) {
            if (item.is_osd === 1) {
              setState(prev => ({ ...prev, osdDocType: item.type_name }));
              return false;
            }
            return true;
          }
          return false;
        });

        setState(prev => ({
          ...prev,
          artifactsList: activeArtifactsList,
          activeArtifactsList: activeArtifactsList
        }));
      } else {
        error("Oops! error occurred during fetching artifacts list!");
      }
    } catch (err) {
      error("Oops! error occurred during fetching artifacts list!");
    }
  }, []);

  useEffect(() => {
    checkAccess(clientId);
    getArtifactsList();
    fetchCompanyDetails();

    if (location.state) {
      setState(prev => ({ 
        ...prev, 
        partnerDocument: true 
      }));
      handleChange(null, location.state.order.bol_number);
    }
  }, [clientId, location.state]);

  useEffect(() => {
    if (clientId) {
      checkAccess(clientId);
      getArtifactsList();
      fetchCompanyDetails();
      setState(prev => ({
        ...prev,
        orderNoList: [],
        bolList: []
      }));
    }
  }, [clientId]);

  const setPreview = useCallback(url => {
    setState(prev => ({ ...prev, pdfLoading: true }));

    fetch(url, { method: "GET" })
      .then(res => res.blob())
      .then(blob => {
        const file = new Blob([blob], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        setState(prev => ({ ...prev, previewDoc: fileURL }));
      })
      .catch(err => console.error(err));
  }, []);

  const handleChange = useCallback((orderId, bolNumber) => {
    if (!orderId && !bolNumber) {
      setState(prev => ({ 
        ...prev, 
        trimmedOrderList: [], 
        orderNo: "", 
        bolNumber: "", 
        previewDoc: '' 
      }));
      return;
    }

    setState(prev => ({ 
      ...prev, 
      orderNo: orderId, 
      bolNumber: bolNumber,
      clicked: true, 
      loading: true, 
      csvShipmentList: [], 
      previewDoc: '' 
    }));

    secureFetch(endpoint.searchShipmentListForCarrier, {
      exp1: '',
      exp2: '',
      bol_Number: bolNumber,
      orderNo: orderId,
      onlyCarrier: state.partnerDocument,
      onlyHaveDocumnet: state.orderWithDocuments,
      osd: false
    }).then(([ok, status, json]) => {
      if (ok) {
        setState(prev => ({
          ...prev,
          selectedPage: 1,
          orderList: json.orderList,
          clicked: false,
          loading: false,
          trimmedOrderList: json.orderList,
          totalPages: Math.ceil(json.orderList.length / 10),
          count: Math.min(10, Math.ceil(json.orderList.length / 10))
        }));
      } else {
        error("Oops! error occurred during search order list!");
      }
    });
  }, [state.partnerDocument, state.orderWithDocuments]);

  const renderOrderList = () => {
    if (state.trimmedOrderList.length === 0 && !state.loading) {
      return (
        <div className="ml2 mt3 f4 tc">
          {state.partnerDocument ? "Please select bol number" : "Please select any filter criteria"}
        </div>
      );
    }

    return (
      <div id="scrollTop" style={{ height }}>
        {state.loading ? (
          <div className="flex justify-center mt6">
            <Loading size="lg">Fetching shipment data. Hang on!</Loading>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {state.trimmedOrderList.map((item, index) => (
              <div
                key={`${item.OrderNo}-${index}`}
                className={`
                  bg-white rounded-lg shadow-sm border border-gray-100
                  transition-all duration-200 ease-in-out
                  hover:shadow-md hover:border-gray-200
                  ${state.selectedOrders[index] ? "bg-red-50" : ""}
                `}
              >
                <ListItems
                  allowRatingsArtifax={Access.allow_ratings_artifax}
                  itemIndex={index}
                  documents={item.document || {}}
                  os={item.os}
                  ordderNo={item.OrderNo}
                  stop={item.Stop}
                  StopType={item.StopType}
                  StopName={item.StopName}
                  address={item.Address}
                  shipmentTime={item.StopArriveTime}
                  stopId={item.StopId}
                  orderNoList={state.orderNoList}
                  artifactsList={state.artifactsList}
                  activeArtifactsList={state.activeArtifactsList}
                  searchOrderNo={state.orderNo}
                  searchBolNumber={state.bolNumber}
                  bolNumber={item.bol_number}
                  getShipmentList={handleChange}
                  setPreview={setPreview}
                  osdDocType={state.osdDocType}
                  checkOs={state.OS}
                  autoCloseInd={item.auto_close_ind}
                  autoCloseDate={item.auto_close_date}
                  driverCloseTs={item.driver_close_ts}
                  sdnRecordId={item.s_rec}
                  driver1EmpId={item.Driver1}
                  driver2EmpId={item.Driver2}
                  driver1UserId={item.driver1UserId}
                  driver2UserId={item.driver2UserId}
                  driver1Name={item.Driver1Name}
                  driver2Name={item.Driver2Name}
                  driver1Email={item.Driver1email}
                  driver2Email={item.Driver2email}
                  driver1Phone={item.Driver1phone}
                  driver2Phone={item.Driver2phone}
                  clientId={item.transporter_client_id || item.client_id}
                  recordSource={item.transporter_client_name || ''}
                  comments={item.Comment}
                  pushNotificationCount={item.pushNotificationCount}
                  isBroker={state.isBroker}
                  allowBrokersToReopenClosedStop={item.allow_brokers_to_reopen_closed_stop}
                  allowBrokersToSendMessage={item.allow_brokers_to_send_message}
                  allowSendMessage={item.allow_send_message}
                  allowBrokerPushNotification={state.isBroker ? Access.allow_broker_push_notification : 1}
                  partnerDocument={!!item.transporter_client_id}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFilterSection = () => (
    <div className="flex" key={clientId}>
      <div className="flex pr-4">
        <Async
          className='f5'
          name="form-field-name"
          style={{ width: "200px" }}
          placeholder="Search order"
          value={state.orderNo ? { label: state.orderNo, value: state.orderNo } : null}
          onChange={(item) => handleChange(item?.value || "", state.bolNumber)}
          options={state.orderNoList}
          loadOptions={debounce(getOrderNO, 500)}
        />
      </div>

      <div className="flex pr-4">
        <Async
          name="form-field-name"
          style={{ width: "200px" }}
          placeholder="Search by BOL"
          value={state.bolNumber ? { label: state.bolNumber, value: state.bolNumber } : null}
          onChange={(item) => handleChange(state.orderNo, item?.value || "")}
          options={state.bolList}
          loadOptions={debounce(getBolList, 500)}
        />
      </div>

      {state.isBroker === 1 && Access.allow_broker_carrier_data_docs === 1 && (
        <div className="flex justify-center items-center px-2">
          <Switch 
            color="success" 
            onChange={() => {
              setState(prev => ({ 
                ...prev, 
                trimmedOrderList: [], 
                orderNo: '', 
                bolNumber: '', 
                partnerDocument: !prev.partnerDocument 
              }));
            }}
            checked={state.partnerDocument}
          />
          <Text h5 css={{pl: 5}}>Partner Document</Text>
        </div>
      )}

      <div className="flex justify-center items-center px-2">
        <Switch 
          color="success" 
          checked={state.orderWithDocuments}
          onChange={() => {
            setState(prev => ({ 
              ...prev, 
              trimmedOrderList: [], 
              orderNo: '', 
              bolNumber: '', 
              orderWithDocuments: !prev.orderWithDocuments 
            }));
          }}
        />
        <Text h5 css={{pl: 5}}>Only include stop with documents</Text>
      </div>
    </div>
  );

  if (Access.allow_ratings_artifax === 0) {
    return (
      <div>
        <SubPageHeader>Shipment Documents</SubPageHeader>
        <div className="ml2 mt3 f5">This feature is unavailable</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between p-4 border-y-2 border-dotted">
        <SubPageHeader>Shipment Documents</SubPageHeader>
        {renderFilterSection()}
      </div>
      <div className="flex">
        <div className="w-50 overflow-y-auto vh-till-bottom">
          {renderOrderList()}
        </div>
        <div className="w-50 flex flex-column items-center mt1 mr2 bg-near-white relative">
          <iframe
            src={state.previewDoc}
            className="vh-till-bottom-doc"
            onLoad={() => setState(prev => ({ ...prev, pdfLoading: false }))}
            width="100%"
            style={{ border: 0 }}
            frameBorder="0"
          />
          {state.pdfLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
              <div className="flex flex-col items-center space-y-3">
                <Loading size="lg" color="primary">
                  Loading document...
                </Loading>
                <div className="text-sm text-gray-600 animate-pulse">
                  Please wait while we fetch your document
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  clientId: state.client?.id || mstore.getPath(["user", "company_id"]),
  Access: state.navigation.Access
});

export default connect(mapStateToProps)(ShipmentDocuments);